import { Optional } from 'types';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Severity, usePushNotifications } from 'services/NotificationService';
import { useCreativeTemplatesBuilderStore } from 'stores/CreativeTemplatesBuilderStore';
import {
  AiImageStyleTypeEnum,
  ImageTypeEnum,
  useArticleGroupAiImagesJobsInProgressLazyQuery,
  useArticleGroupAiImagesLazyQuery,
  useCreateJobsArticleGroupAiImagesCreateJobMutation
} from 'api';

export const useAiImages = () => {
  const { id } = useParams<{ id: string }>();
  const store = useCreativeTemplatesBuilderStore();

  const pushNotification = usePushNotifications();

  const [jobInProgress, setJobInProgress] = useState(false);
  const [styleType, setStyleType] = useState<Optional<{ key: string; label: string }>>(null);

  const [articleGroupAiImageLazyQuery, { data: articleGroupAiImagesData }] = useArticleGroupAiImagesLazyQuery({ id });
  const [articleGroupAiImagesJobsInProgressLazyQuery, { data: job }] = useArticleGroupAiImagesJobsInProgressLazyQuery();
  const [createJobMutation, { data: createJobData, loading: createJobLoading }] =
    useCreateJobsArticleGroupAiImagesCreateJobMutation();

  const handleSelectStyleType = (styleTypeOption: Optional<{ key: string; label: string }>) => {
    setStyleType(styleTypeOption);
  };

  const fetchJobStatus = () => {
    articleGroupAiImagesJobsInProgressLazyQuery({ variables: { id } });
  };

  const handleGenerateMoreImages = async () => {
    try {
      await createJobMutation({
        variables: { articleGroupId: id, styleType: styleType ? (styleType.key as AiImageStyleTypeEnum) : null }
      });
    } catch (e) {
      pushNotification({ message: `Something went wrong: ${e.message}`, title: 'Error', severity: Severity.ERROR });
    }
  };

  useEffect(() => {
    fetchJobStatus();
  }, []);

  useEffect(() => {
    if (!createJobData || !createJobData.createJobsArticleGroupAiImagesCreateJob) return;

    if (createJobData.createJobsArticleGroupAiImagesCreateJob.jobsArticleGroupAiImagesCreateJob) {
      fetchJobStatus();

      pushNotification({
        message: 'Generate ai images process successfully created',
        title: 'Success',
        severity: Severity.SUCCESS
      });
    }

    if (createJobData.createJobsArticleGroupAiImagesCreateJob.errors) {
      pushNotification({
        message: `Something went wrong: ${createJobData.createJobsArticleGroupAiImagesCreateJob.errors.join(', ')}`,
        title: 'Error',
        severity: Severity.ERROR
      });
    }
  }, [createJobData]);

  useEffect(() => {
    if (!job) return;

    if (job.articleGroup.hasArticleGroupsAiImagesJobsInProgress) {
      setJobInProgress(true);
    } else {
      articleGroupAiImageLazyQuery();
      setJobInProgress(false);
      return;
    }

    const timeout = setTimeout(() => fetchJobStatus(), 5000);

    return () => clearTimeout(timeout);
  }, [job]);

  useEffect(() => {
    if (!articleGroupAiImagesData) return;

    const articleGroupAiImages =
      articleGroupAiImagesData.articleGroup.aiImages.map((image) => ({
        id: image.id,
        url: image.url,
        ctr: image.metrics?.ctr || 0,
        spent: image.metrics?.spent || 0,
        type: ImageTypeEnum.AiImage,
        imageType: null
      })) || [];

    store.setImages([...store.images.filter((image) => image.type !== 'AiImage'), ...articleGroupAiImages]);
  }, [articleGroupAiImagesData]);

  return { handleGenerateMoreImages, handleSelectStyleType, styleType, jobInProgress, createJobLoading };
};
